import { CSRF_HEADER, type EntityUsage } from "@esi/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Ellipsis, FolderTree, Move, Pencil, Plus, Trash2 } from "lucide-react";
import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { NativeSelect } from "@/components/NativeSelect";
import { Notice } from "@/components/Notice";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiError, apiError, ok } from "../api-errors.ts";
import { useSession } from "../session.tsx";
import { useAdminApi } from "./admin-api.tsx";
import {
  adminRefusal,
  inSubtree,
  type TreeRow,
  treeRows,
  usageLines,
  useAdminEntities,
} from "./entity-tree.ts";

type Action = "create" | "rename" | "move" | "delete";

/**
 * The entity tree (spec `tenant-administration` "Entity tree editing"): every entity the caller
 * administers, indented under its parent. Entities below the caller's own can be renamed, moved
 * (never under themselves) and deleted when empty; the root can be renamed by tenant admins.
 * Presentation only: the server checks every change.
 */
export function EntitiesAdmin() {
  const { t } = useTranslation();
  const entities = useAdminEntities();
  const [open, setOpen] = useState<{
    action: Action;
    row: TreeRow;
    trigger: HTMLElement | null;
  } | null>(null);

  if (entities.isError) {
    return (
      <div data-esi-content="people" className="flex flex-col gap-8">
        <PageHeader title={t("entities.title")} lead={t("entities.lead")} />
        <Notice
          tone={
            entities.error instanceof ApiError && entities.error.status === 403
              ? "neutral"
              : "danger"
          }
        >
          {entities.error instanceof ApiError && entities.error.status === 403
            ? t("entities.adminsOnly")
            : entities.error.message}
        </Notice>
      </div>
    );
  }
  const rows = entities.data ? treeRows(entities.data) : null;
  return (
    <div data-esi-content="people" className="flex flex-col gap-8">
      <PageHeader title={t("entities.title")} lead={t("entities.lead")} />
      {rows ? (
        <ul
          aria-label={t("entities.title")}
          className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface-raised"
        >
          {rows.map((row) => (
            <EntityRow
              key={row.entity.id}
              row={row}
              onAction={(action, trigger) => setOpen({ action, row, trigger })}
            />
          ))}
        </ul>
      ) : (
        <Skeleton className="h-40 w-full" />
      )}
      {open && rows ? (
        <EntityDialog
          action={open.action}
          row={open.row}
          rows={rows}
          returnFocus={open.trigger}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </div>
  );
}

function EntityRow(props: {
  row: TreeRow;
  onAction: (action: Action, trigger: HTMLElement | null) => void;
}) {
  const { t } = useTranslation();
  const { readOnly } = useAdminApi();
  const trigger = useRef<HTMLButtonElement>(null);
  const tenantAdmin = useSession().roles.includes("tenant_admin");
  const { entity, depth, top } = props.row;
  const isRoot = entity.parentId === null;
  // Entities below the caller's own; the root's name too, for tenant admins.
  const editable = !top;
  const renamesRoot = isRoot && tenantAdmin;
  return (
    <li
      className="flex items-center gap-3 py-2.5 pr-2"
      style={{ paddingLeft: `${1 + depth * 1.5}rem` }}
    >
      <FolderTree aria-hidden="true" className="size-4 shrink-0 text-fg-muted" strokeWidth={1.5} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium text-fg">{entity.name}</span>
        {isRoot ? <span className="text-xs text-fg-muted">{t("entities.root")}</span> : null}
      </span>
      {readOnly ? null : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              ref={trigger}
              variant="ghost"
              size="icon-sm"
              aria-label={t("entities.actionsOf", { name: entity.name })}
            >
              <Ellipsis aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-48">
            <DropdownMenuItem onSelect={() => props.onAction("create", trigger.current)}>
              <Plus aria-hidden="true" strokeWidth={1.75} />
              {t("entities.create")}
            </DropdownMenuItem>
            {editable || renamesRoot ? (
              <DropdownMenuItem onSelect={() => props.onAction("rename", trigger.current)}>
                <Pencil aria-hidden="true" strokeWidth={1.75} />
                {t("entities.rename")}
              </DropdownMenuItem>
            ) : null}
            {editable ? (
              <DropdownMenuItem onSelect={() => props.onAction("move", trigger.current)}>
                <Move aria-hidden="true" strokeWidth={1.75} />
                {t("entities.move")}
              </DropdownMenuItem>
            ) : null}
            {editable ? (
              <DropdownMenuItem
                variant="destructive"
                onSelect={() => props.onAction("delete", trigger.current)}
              >
                <Trash2 aria-hidden="true" strokeWidth={1.75} />
                {t("entities.delete")}
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </li>
  );
}

function EntityDialog(props: {
  action: Action;
  row: TreeRow;
  rows: readonly TreeRow[];
  returnFocus: HTMLElement | null;
  onClose: () => void;
}) {
  const { admin } = useAdminApi();
  const { t } = useTranslation();
  const id = useId();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const { entity } = props.row;
  const [name, setName] = useState(props.action === "rename" ? entity.name : "");
  // A new parent: any entity in scope outside the moved subtree, other than the current one.
  const parents = props.rows.filter((r) => !inSubtree(r.entity, entity));
  const [parentId, setParentId] = useState<string>(
    parents.find((r) => r.entity.id !== entity.parentId)?.entity.id ?? "",
  );
  const headers = { headers: { [CSRF_HEADER]: csrfToken } };
  const param = { entityId: entity.id };
  const run = useMutation({
    mutationFn: async (): Promise<unknown> => {
      switch (props.action) {
        case "create":
          return ok(
            await admin.entities.$post(
              { json: { parentId: entity.id, name: name.trim() } },
              headers,
            ),
          );
        case "rename":
          return ok(
            await admin.entities[":entityId"].$patch(
              { param, json: { name: name.trim() } },
              headers,
            ),
          );
        case "move":
          return ok(
            await admin.entities[":entityId"].$patch({ param, json: { parentId } }, headers),
          );
        case "delete": {
          const res = await admin.entities[":entityId"].$delete({ param }, headers);
          if (!res.ok) {
            throw await apiError(res);
          }
          return null;
        }
      }
    },
    onSuccess: async () => {
      // Names and paths show up in users (memberships) and settings (sources and locks) too.
      await queryClient.invalidateQueries({ queryKey: ["admin"] });
      props.onClose();
    },
  });
  const title = {
    create: t("entities.createTitle", { name: entity.name }),
    rename: t("entities.renameTitle", { name: entity.name }),
    move: t("entities.moveTitle", { name: entity.name }),
    delete: t("entities.deleteTitle", { name: entity.name }),
  }[props.action];
  const usage =
    run.error instanceof ApiError && run.error.code === "entity_not_empty"
      ? usageLines(run.error.details as Partial<EntityUsage> | undefined)
      : [];
  const disabled =
    run.isPending ||
    ((props.action === "create" || props.action === "rename") &&
      (name.trim() === "" || name.trim() === (props.action === "rename" ? entity.name : ""))) ||
    (props.action === "move" && (parentId === "" || parentId === entity.parentId));

  return (
    <AppDialog
      open
      title={title}
      returnFocus={props.returnFocus}
      description={
        props.action === "move"
          ? t("entities.moveBody")
          : props.action === "delete"
            ? t("entities.deleteBody")
            : undefined
      }
      onClose={props.onClose}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!disabled) {
            run.mutate();
          }
        }}
      >
        {props.action === "create" || props.action === "rename" ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-name`}>{t("entities.name")}</Label>
            <Input
              id={`${id}-name`}
              value={name}
              maxLength={200}
              autoFocus
              onChange={(e) => {
                run.reset();
                setName(e.currentTarget.value);
              }}
            />
          </div>
        ) : null}
        {props.action === "move" ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-parent`}>{t("entities.newParent")}</Label>
            <NativeSelect
              id={`${id}-parent`}
              value={parentId}
              onChange={(e) => {
                run.reset();
                setParentId(e.currentTarget.value);
              }}
            >
              {parents.map((r) => (
                <option key={r.entity.id} value={r.entity.id}>
                  {`${" ".repeat(r.depth)}${r.entity.name}`}
                  {r.entity.id === entity.parentId ? ` (${t("entities.currentParent")})` : ""}
                </option>
              ))}
            </NativeSelect>
          </div>
        ) : null}
        {run.error ? (
          <Notice tone="danger">
            <p>{adminRefusal(run.error)}</p>
            {usage.length > 0 ? (
              <ul className="mt-1 list-disc pl-5">
                {usage.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            ) : null}
          </Notice>
        ) : null}
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" onClick={props.onClose}>
            {t("app.cancel")}
          </Button>
          <Button
            type="submit"
            variant={props.action === "delete" ? "destructive" : "default"}
            disabled={disabled}
          >
            {
              {
                create: t("entities.createConfirm"),
                rename: t("entities.renameConfirm"),
                move: t("entities.moveConfirm"),
                delete: t("entities.deleteConfirm"),
              }[props.action]
            }
          </Button>
        </div>
      </form>
    </AppDialog>
  );
}
